// Webflow's contact form markup, submitted to our own Pages Function (functions/api/contact.ts:
// stored in D1, emailed via SendGrid) instead of Webflow's form backend.
// Mirrors Webflow's behaviour: button shows data-wait text, then the form is swapped
// for .w-form-done on success or .w-form-fail is shown on error.
import { useState, type FormEvent } from "react";
import Turnstile from "@/components/Turnstile";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Keep webflow.js's delegated form handler from also posting to Webflow.
    e.stopPropagation();
    const data = new FormData(e.currentTarget);
    if (data.get("company")) return; // honeypot
    setStatus("submitting");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          message: data.get("Message"),
          // Turnstile writes its token into a hidden `cf-turnstile-response` input.
          turnstileToken: data.get("cf-turnstile-response") ?? undefined,
        }),
      });
      setStatus(res.ok ? "success" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="form-block w-form">
      <form
        id="email-form"
        name="email-form"
        data-name="Email Form"
        className="form"
        onSubmit={onSubmit}
        style={status === "success" ? { display: "none" } : undefined}
      >
        <input className="text-field w-input" maxLength={256} name="name" data-name="Name" placeholder="Name" type="text" id="name" />
        <input className="text-field w-input" maxLength={256} name="email" data-name="Email" placeholder="Email" type="email" id="email" required />
        <textarea placeholder="Message" maxLength={5000} id="Message" name="Message" data-name="Message" className="textarea w-input" />
        {/* Honeypot: hidden from people, bots fill it in. */}
        <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ display: "none" }} />
        {/* Stands in for the live site's reCAPTCHA box; renders nothing until VITE_TURNSTILE_SITE_KEY is set. */}
        <Turnstile />
        <input
          type="submit"
          className="submit-button w-button"
          value={status === "submitting" ? "Please wait..." : "Submit"}
          disabled={status === "submitting"}
        />
        {/* GDPR notice at the point of collection, kept deliberately low-key. */}
        <p style={{ margin: ".9em 0 0", fontSize: ".8em", lineHeight: "150%", color: "var(--paragraph)", opacity: 0.7 }}>
          We use your details only to reply to you.{" "}
          <a href="/privacy-policy" style={{ color: "inherit", textDecoration: "underline" }}>Privacy Policy</a>
        </p>
      </form>
      <div className="success-message w-form-done" style={status === "success" ? { display: "block" } : undefined}>
        <div className="text-success">Thank you! Your submission has been received!</div>
      </div>
      <div className="error-message w-form-fail" style={status === "error" ? { display: "block" } : undefined}>
        <div className="text-error">Oops! Something went wrong while submitting the form.</div>
      </div>
    </div>
  );
}
